/**
 * Звуковая панель: REST-вызовы, адреса файлов и подготовка загружаемого звука.
 * Сервер принимает MP3, OGG и WAV до 512 КБ и 5,2 секунды; длинный файл клиент обрезает сам
 * и отправляет выбранный кусок в WAV (моно, 16 бит — пять секунд как раз влезают в 512 КБ).
 */
import { authed } from './auth'
import type { SoundboardSound } from '@/types/api'

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1'

export const SOUND_MAX_BYTES = 512 * 1024
export const SOUND_MAX_SECONDS = 5.2
/** Форматы, которые сервер примет как есть. */
const SERVER_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/ogg', 'audio/wav', 'audio/x-wav', 'audio/wave']

/**
 * Файл звука: стандартные и звуки серверов — по одному адресу. У стандартных (id до 999) метка v=2:
 * первые, синтезированные, отдавались с кэшем на сутки, и браузер играл бы их вместо записей.
 * Теперь сервер отдаёт стандартные с no-cache и ETag, метку менять не нужно.
 */
export function soundUrl(id: string): string {
  return id.length < 4 ? `${API_BASE}/soundboard-sounds/${id}?v=2` : `${API_BASE}/soundboard-sounds/${id}`
}

export interface SoundInput {
  name?: string
  volume?: number
  emoji_id?: string | null
  emoji_name?: string | null
}

export const soundboardApi = {
  defaults: () => authed<SoundboardSound[]>('/soundboard-default-sounds'),
  list: (guildId: string) => authed<{ items: SoundboardSound[] }>(`/guilds/${guildId}/soundboard-sounds`).then((r) => r.items),
  /** sound — data URI файла. */
  create: (guildId: string, body: SoundInput & { name: string; sound: string }) =>
    authed<SoundboardSound>(`/guilds/${guildId}/soundboard-sounds`, { method: 'POST', body: JSON.stringify(body) }),
  update: (guildId: string, soundId: string, body: SoundInput) =>
    authed<SoundboardSound>(`/guilds/${guildId}/soundboard-sounds/${soundId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (guildId: string, soundId: string) => authed<void>(`/guilds/${guildId}/soundboard-sounds/${soundId}`, { method: 'DELETE' }),
  /** Сыграть звук в голосовом канале, где сейчас сам. */
  send: (channelId: string, sound: SoundboardSound) =>
    authed<void>(`/channels/${channelId}/send-soundboard-sound`, {
      method: 'POST',
      body: JSON.stringify({ sound_id: sound.sound_id, source_guild_id: sound.guild_id ?? null }),
    }),
}

/** Разобранный файл: звук для прослушивания и обрезки, длительность и можно ли отправить как есть. */
export interface DecodedSound {
  buffer: AudioBuffer
  duration: number
  /** Файл подходит серверу без переделки: формат, размер и длина. */
  asIs: boolean
  file: File
}

/** Разобрать выбранный файл: любой формат, который умеет браузер (MP3, OGG, WAV, M4A…). */
export async function decodeSoundFile(file: File): Promise<DecodedSound> {
  const bytes = await file.arrayBuffer()
  const ctx = new OfflineAudioContext(1, 1, 48000)
  const buffer = await ctx.decodeAudioData(bytes.slice(0))
  const asIs = SERVER_TYPES.includes(file.type) && file.size <= SOUND_MAX_BYTES && buffer.duration <= SOUND_MAX_SECONDS
  return { buffer, duration: buffer.duration, asIs, file }
}

/** Кусок звука [start, end) в WAV: моно, 16 бит, частота исходного буфера. */
export function encodeWav(buffer: AudioBuffer, start: number, end: number): Blob {
  const rate = buffer.sampleRate
  const from = Math.max(0, Math.floor(start * rate))
  const to = Math.min(buffer.length, Math.ceil(end * rate))
  const n = Math.max(0, to - from)
  const mono = new Float32Array(n)
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c)
    for (let i = 0; i < n; i++) mono[i]! += (data[from + i] ?? 0) / buffer.numberOfChannels
  }
  // короткие спады по краям: обрезка посреди звука иначе щёлкает
  const fade = Math.min(Math.floor(n / 4), Math.floor(0.01 * rate))
  for (let i = 0; i < fade; i++) {
    mono[i]! *= i / fade
    mono[n - 1 - i]! *= i / fade
  }
  const out = new DataView(new ArrayBuffer(44 + n * 2))
  const text = (at: number, s: string) => [...s].forEach((ch, i) => out.setUint8(at + i, ch.charCodeAt(0)))
  text(0, 'RIFF')
  out.setUint32(4, 36 + n * 2, true)
  text(8, 'WAVEfmt ')
  out.setUint32(16, 16, true)
  out.setUint16(20, 1, true)
  out.setUint16(22, 1, true)
  out.setUint32(24, rate, true)
  out.setUint32(28, rate * 2, true)
  out.setUint16(32, 2, true)
  out.setUint16(34, 16, true)
  text(36, 'data')
  out.setUint32(40, n * 2, true)
  for (let i = 0; i < n; i++) out.setInt16(44 + i * 2, Math.max(-1, Math.min(1, mono[i]!)) * 0x7fff, true)
  return new Blob([out], { type: 'audio/wav' })
}

export function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('Не удалось прочитать файл'))
    reader.readAsDataURL(blob)
  })
}

/** Огибающая для рисования волны: максимум модуля в каждом из n отрезков, 0…1. */
export function waveform(buffer: AudioBuffer, n: number): number[] {
  const data = buffer.getChannelData(0)
  const step = Math.max(1, Math.floor(data.length / n))
  const peaks: number[] = []
  let top = 0
  for (let i = 0; i < n; i++) {
    let m = 0
    for (let j = i * step; j < Math.min(data.length, (i + 1) * step); j++) m = Math.max(m, Math.abs(data[j]!))
    peaks.push(m)
    top = Math.max(top, m)
  }
  return peaks.map((p) => (top > 0 ? p / top : 0))
}

/** Имя звука из имени файла: без расширения и подчёркиваний, не длиннее 32 символов. */
export function nameFromFile(file: File): string {
  return file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim().slice(0, 32)
}
